import { useMemo } from 'react';

import { paths } from 'src/routes/paths';
import { useTranslate } from 'src/locales';
import { usePathname, useSearchParams } from 'src/routes/hooks';
import { usePermissions } from 'src/auth/hooks/use-permissions';

import SvgColor from 'src/components/svg-color';
import type { NavItemBaseProps } from 'src/components/nav-section/types';

// ----------------------------------------------------------------------

const icon = (name: string) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />;

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  document: icon('ic_file'), // Usar ícone de arquivo para documentos
};

export type NavSection = {
  subheader: string;
  items: NavItemBaseProps[];
};

const getNavItemSortPriority = (item: NavItemBaseProps): number => {
  if (item.path === paths.dashboard.faq.root) {
    return -1;
  }

  return 0;
};

const shouldPreserveChildrenOrder = (item: NavItemBaseProps): boolean =>
  item.path === paths.dashboard.notifications.campaigns || item.path === paths.dashboard.notifications.proximity;

const sortNavItemsAlphabetically = (items: NavItemBaseProps[]): NavItemBaseProps[] =>
  [...items]
    .map((item) => ({
      ...item,
      children:
        Array.isArray(item.children) && !shouldPreserveChildrenOrder(item)
          ? sortNavItemsAlphabetically(item.children as NavItemBaseProps[])
          : item.children,
    }))
    .sort((itemA, itemB) => {
      const priorityDifference = getNavItemSortPriority(itemA) - getNavItemSortPriority(itemB);

      if (priorityDifference !== 0) {
        return priorityDifference;
      }

      return itemA.title.localeCompare(itemB.title, 'pt-BR', { sensitivity: 'base' });
    });

const sortNavSectionsItemsAlphabetically = (sections: NavSection[]): NavSection[] =>
  sections.map((section) => ({
    ...section,
    items: sortNavItemsAlphabetically(section.items),
  }));

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useTranslate();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasDuplicate = searchParams.get('duplicate') !== null;

  // Hook para verificar permissões
  const { hasMotorFeature, hasAppFeature, hasScanFeature, checkGenericModule } = usePermissions();

  const data = useMemo(() => {
    const navSections = [];

    // ===================================================================
    // SEÇÃO MOTOR DE PONTOS (só aparece se tem feature 'motor')
    // ===================================================================
    if (hasMotorFeature()) {
      const motorItems = [];

      // Subseção: Geração de pontos
      const points_gen_children = [];

      if (checkGenericModule('motorRules', 'view')) {
        points_gen_children.push({
          title: t('Gerenciar Regras'),
          path: paths.dashboard.rules.root,
        });
      }

      if (checkGenericModule('motorConversionRates', 'view')) {
        points_gen_children.push({
          title: t('Taxas de Conversão'),
          path: paths.dashboard.conversionRate.root,
        });
      }

      // Só adiciona o grupo se tem pelo menos um item
      if (points_gen_children.length > 0) {
        motorItems.push({
          title: t('Geração de pontos'),
          path: '#',
          icon: ICONS.invoice,
          activePaths: [paths.dashboard.variables],
          children: points_gen_children,
        });
      }

      // Subseção: Sorteios
      const giveaways_campaings_children = [];

      if (checkGenericModule('motorGiveaways', 'view')) {
        giveaways_campaings_children.push({
          title: t('Gerenciar Sorteios'),
          path: paths.dashboard.giveaways.root,
        });
      }

      // Adiciona a seção Motor se tem pelo menos um item
      if (motorItems.length > 0) {
        navSections.push({
          subheader: t('Motor de pontos'),
          items: motorItems,
        });
      }
    }

    // ===================================================================
    // SEÇÃO SCAN DE NOTAS (só aparece se tem feature 'scan')
    // ===================================================================
    if (hasScanFeature()) {
      const scanItems = [];

      // Item direto: Notas Fiscais
      if (checkGenericModule('invoices', 'view')) {
        scanItems.push({
          title: t('Notas Fiscais'),
          path: paths.dashboard.invoices.root,
          icon: ICONS.invoice,
        });
      }

      // Adiciona a seção Scan se tem pelo menos um item
      if (scanItems.length > 0) {
        navSections.push({
          subheader: t('Scan de Notas'),
          items: scanItems,
        });
      }
    }

    // ===================================================================
    // SEÇÃO APP (só aparece se tem feature 'app')
    // ===================================================================
    if (hasAppFeature()) {
      const appItems = [];

      // Ordem: Estabelecimentos, Benefícios, Campanhas, Home, Destaques, Fique por Dentro, Banners, Usuários do App, Documentos, Configurações

      if (
        checkGenericModule('appStoresEstablishments', 'view') ||
        checkGenericModule('appFoodEstablishments', 'view') ||
        checkGenericModule('appServiceEstablishments', 'view')
      ) {
        appItems.push({
          title: t('Estabelecimentos'),
          path: paths.dashboard.establishments.root,
          icon: ICONS.ecommerce,
          activePaths: [paths.dashboard.establishments.categories],
        });
      }

      // 2. Subseção: Benefícios
      const benefits_children = [];

      if (checkGenericModule('appEvents', 'view')) {
        benefits_children.push({
          title: t('Eventos'),
          path: paths.dashboard.events.root,
        });
      }

      if (checkGenericModule('appCoupons', 'view')) {
        benefits_children.push({
          title: t('Cupons'),
          path: paths.dashboard.coupons.root,
        });
      }

      if (checkGenericModule('appProducts', 'view')) {
        benefits_children.push({
          title: t('Produtos'),
          path: paths.dashboard.products.root,
        });
      }

      if (checkGenericModule('appBenefitRedeems', 'view')) {
        benefits_children.push({
          title: t('Resgates'),
          path: paths.dashboard.redeems.root,
        });
      }

      if (benefits_children.length > 0) {
        appItems.push({
          title: t('Benefícios'),
          path: '#',
          icon: ICONS.label,
          children: benefits_children,
        });
      }

      // 3. Item direto: Campanhas
      if (checkGenericModule('motorCampaigns', 'view')) {
        appItems.push({
          title: t('Campanhas'),
          path: paths.dashboard.campaigns.root,
          icon: ICONS.calendar,
        });
      }

      // 4. Item direto: Home
      if (checkGenericModule('appHome', 'view')) {
        appItems.push({
          title: t('Home'),
          path: paths.dashboard.home.root,
          icon: ICONS.dashboard,
        });
      }

      // 5. Item direto: Destaques
      if (checkGenericModule('appHighlights', 'view')) {
        appItems.push({
          title: t('Destaques'),
          path: paths.dashboard.highlights.root,
          icon: ICONS.job,
        });
      }

      // 6. Item direto: Fique por dentro
      if (checkGenericModule('appKeepUps', 'view')) {
        appItems.push({
          title: t('Fique por dentro'),
          path: paths.dashboard.keepUp.root,
          icon: ICONS.blog,
        });
      }

      // Subseção: Stories
      if (checkGenericModule('appStories', 'view')) {
        appItems.push({
          title: t('Stories'),
          path: paths.dashboard.stories.root,
          icon: ICONS.order,
        });
      }

      // 7. Subseção: Banners
      if (checkGenericModule('appBanners', 'view')) {
        appItems.push({
          title: t('Banners'),
          path: paths.dashboard.banners.root,
          icon: ICONS.product,
        });
      }

      // 8. Item: Usuários do App
      if (checkGenericModule('appUsers', 'view')) {
        appItems.push({
          title: t('Usuários do App'),
          path: paths.dashboard.appUsers.root,
          icon: ICONS.user,
        });
      }

      // 8.1 Notificações (com submenu)
      const canViewNotifications = checkGenericModule('appNotifications', 'view');
      const canViewProximityNotifications = checkGenericModule('appProximityNotification', 'view');
      if (canViewNotifications || canViewProximityNotifications) {
        const notificationsChildren = [];

        if (canViewNotifications) {
          notificationsChildren.push({
            title: t('Campanhas de Notificação'),
            path: paths.dashboard.notifications.campaigns,
          });
        }

        if (canViewProximityNotifications) {
          notificationsChildren.push({
            title: t('Notif. por Proximidade'),
            path: paths.dashboard.notifications.proximity,
          });
        }

        if (canViewNotifications) {
          notificationsChildren.push({
            title: t('Segmentos de Audiência'),
            path: paths.dashboard.notifications.segments,
          });

          notificationsChildren.push({
            title: t('Logs de Notificações'),
            path: paths.dashboard.notifications.logs,
          });
        }

        appItems.push({
          title: t('Notificações'),
          path: canViewNotifications ? paths.dashboard.notifications.campaigns : paths.dashboard.notifications.proximity,
          icon: ICONS.chat,
          children: notificationsChildren,
        });
      }

      // 9. Subseção: Documentos
      const documents_children = [];

      if (checkGenericModule('appTermsOfUse', 'view')) {
        documents_children.push({
          title: t('Termos de Uso'),
          path: paths.dashboard.terms.root,
        });
      }

      if (checkGenericModule('appPrivacyAndPolicy', 'view')) {
        documents_children.push({
          title: t('Política de Privacidade'),
          path: paths.dashboard.privacy.root,
        });
      }

      // FAQ (subnível)
      if (checkGenericModule('appFaqs', 'view')) {
        const faq_children = [];

        faq_children.push({
          title: t('Gerenciar FAQs'),
          path: paths.dashboard.faq.root,
        });

        faq_children.push({
          title: t('Gerenciar Categorias'),
          path: paths.dashboard.faq.categories,
        });

        documents_children.push({
          title: t('FAQ'),
          path: '#',
          children: faq_children,
        });
      }

      if (documents_children.length > 0) {
        appItems.push({
          title: t('Documentos'),
          path: '#',
          icon: ICONS.document,
          children: documents_children,
        });
      }

      // 10. Subseção: Configurações
      const config_children = [];

      if (checkGenericModule('appClientData', 'view')) {
        config_children.push({
          title: t('Dados do Cliente Nectar'),
          path: paths.dashboard.shoppingSettings.root,
        });

        config_children.push({
          title: t('Níveis de Usuário'),
          path: paths.dashboard.userLevels.root,
        });
      }

      if (config_children.length > 0) {
        appItems.push({
          title: t('Configurações'),
          path: '#',
          icon: ICONS.menuItem,
          children: config_children,
        });
      }

      // Adiciona a seção App se tem pelo menos um item
      if (appItems.length > 0) {
        navSections.push({
          subheader: t('Aplicativo'),
          items: appItems,
        });
      }
    }

    // ===================================================================
    // SEÇÃO CONFIGURAÇÕES GERAIS (sem bloqueio de permissões)
    // ===================================================================
    const generalConfigItems = [];

    if (checkGenericModule('adminUsers', 'view')) {
      generalConfigItems.push({
        title: t('Administradores'),
        path: paths.dashboard.adminUsers.root,
        icon: ICONS.user,
        activePaths: [paths.dashboard.adminLogs],
      });
    }

    if (checkGenericModule('webappUsers', 'view')) {
      generalConfigItems.push({
        title: t('Usuários do Web App'),
        path: paths.dashboard.webAppUsers.root,
        icon: ICONS.user,
      });
    }

    // Adiciona a seção Configurações Gerais se tem pelo menos um item
    if (generalConfigItems.length > 0) {
      navSections.push({
        subheader: t('Configurações Gerais'),
        items: generalConfigItems,
      });
    }

    return sortNavSectionsItemsAlphabetically(navSections as NavSection[]);
  }, [t, pathname, hasDuplicate, hasMotorFeature, hasAppFeature, hasScanFeature, checkGenericModule]);

  return data;
}
